import { useEffect, useRef, useState } from 'react'

import { Box, Button, TextField } from '@material-ui/core'
import ExitToApp from '@material-ui/icons/ExitToApp'
import SetState from 'Components/Hooks/SetState'
import useCustomSnackbar from 'Components/UI/Atoms/SnackBar'
import CancelConfirmButtons from 'Components/UI/Molecules/CancelConfirmButtons'
import ComplexDialog from 'Components/UI/Molecules/ComplexDialog'
import ComplexDialogActions from 'Components/UI/Molecules/ComplexDialog/Actions'
import ComplexDialogHistory from 'Components/UI/Molecules/ComplexDialogHistory'
import DialogComponent from 'Components/UI/Molecules/Modals/DialogComponent'
import { SUCURSAL_ORIGEN } from 'Constants'
import { interfaces } from 'service/interfaces'

import useStyles from '../styles'

import Asientos from './Asientos'
import CtaCteClient from './CTACTEClient'
import HomeContent from './Home'

const Edit = (props) => {
  const {
    open,
    handleclose,
    data,
    setRender,
    render,
    setLoading,
    dataBefore,
    fromOrders = false,
  } = props

  const createDismissableSnackbar = useCustomSnackbar()

  const { state, setOpen, setClose } = SetState()

  const [dataInfo, setDataInfo] = useState(data)

  const [historyRows, setHistoryRows] = useState(data?.history || [])
  const [openFollowUpForm, setOpenFollowUpForm] = useState(false)
  const [followUpDescription, setFollowUpDescription] = useState('')
  const [followUpTouched, setFollowUpTouched] = useState(false)
  const [followUpLoading, setFollowUpLoading] = useState(false)
  const prevClientIdRef = useRef(data?.infoClient?.id)

  useEffect(() => {
    const currentClientId = data?.infoClient?.id
    if (prevClientIdRef.current !== currentClientId) {
      setHistoryRows(data?.history || [])
      prevClientIdRef.current = currentClientId
    }
  }, [data])

  const isFollowUpValid = followUpDescription.trim().length > 0

  const formatHistoryDate = (value) => {
    if (!value) return value
    if (typeof value !== 'string') return value
    if (!value.includes('T')) return value
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value

    const pad = (n) => String(n).padStart(2, '0')
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(
      d.getMinutes()
    )}`
  }

  const handleConfirmFollowUp = async () => {
    setFollowUpTouched(true)
    if (!isFollowUpValid) return

    if (!data?.infoClient?.id || !empresaId) return

    setFollowUpLoading(true)
    try {
      const res = await interfaces.salesClients.createFollowUpHistory(data.infoClient.id, {
        empresa: empresaId,
        descripcion: followUpDescription.trim(),
      })

      const row = res?.data
      if (row) {
        setHistoryRows((prev) => [{ ...row, fecha: formatHistoryDate(row.fecha) }, ...(prev || [])])
      }

      setFollowUpDescription('')
      setFollowUpTouched(false)
      setOpenFollowUpForm(false)
      createDismissableSnackbar('Seguimiento agregado', {
        variant: 'success',
      })
    } catch (error) {
      const message =
        error?.response?.data?.data || error?.response?.data?.message || 'Algo salió mal!'
      createDismissableSnackbar(message, {
        variant: 'error',
      })
    } finally {
      setFollowUpLoading(false)
    }
  }

  const getChangedInfoClientFields = (prevInfoClient, nextInfoClient) => {
    if (!prevInfoClient || !nextInfoClient) return {}

    return Object.keys(nextInfoClient).reduce((acc, key) => {
      if (prevInfoClient?.[key] !== nextInfoClient?.[key]) {
        acc[key] = nextInfoClient?.[key]
      }
      return acc
    }, {})
  }

  const handleChange = (property, newData) => {
    setDataInfo((prevDataInfo) => ({
      ...prevDataInfo,
      [property]: newData,
    }))
  }

  const classes = useStyles()

  const origenLabel = SUCURSAL_ORIGEN.find((sucursal) => sucursal.label === data.infoClient.origen)
  const empresaId = origenLabel ? origenLabel.id : null

  const handleDelete = async () => {
    setLoading(true)
    await interfaces.salesClients
      .deleteClient(data.infoClient.id, empresaId)
      .then((res) => {
        createDismissableSnackbar(res.data, {
          variant: 'success',
        })
        setRender(!render)
        handleclose()
      })
      .catch((err) => {
        createDismissableSnackbar(err.response.data.data, {
          variant: 'error',
        })
      })
    setLoading(false)
    setClose()
  }

  const changeStatus = async (estado) => {
    setLoading(true)
    const body = {
      empresa: empresaId,
      cliente_id: data.infoClient.id,
      estado: estado.trim().toUpperCase(),
    }
    await interfaces.salesClients
      .changeStatus(body)
      .then((res) => {
        createDismissableSnackbar(res.data, {
          variant: 'success',
        })
        setRender(!render)
        handleclose()
      })
      .catch(() => {
        createDismissableSnackbar('Algo salió mal!', {
          variant: 'error',
        })
      })
    setLoading(false)
    setClose()
  }

  const getFlatChanges = (original, updated) => {
    let diff = {}

    const compare = (obj1, obj2) => {
      Object.keys(obj2).forEach((key) => {
        if (key === 'history') return

        if (typeof obj2[key] === 'object' && obj2[key] !== null && !Array.isArray(obj2[key])) {
          compare(obj1[key] || {}, obj2[key])
        } else if (obj2[key] !== obj1[key]) {
          diff[key] = obj2[key]
        }
      })
    }

    compare(original, updated)
    return diff
  }

  const handleSubmit = async () => {
    const body = getFlatChanges(data, dataInfo)
    const payload = getChangedInfoClientFields(data?.infoClient, dataInfo?.infoClient)

    if (Object.keys(body).length === 0) {
      return createDismissableSnackbar('No hay cambios para guardar', {
        variant: 'info',
      })
    }

    if (
      Object.prototype.hasOwnProperty.call(payload, 'domicilio') &&
      (!payload.domicilio || payload.domicilio.trim() === '')
    ) {
      return createDismissableSnackbar('La dirección es obligatoria para poder guardar!', {
        variant: 'error',
      })
    }
    setLoading(true)

    await interfaces.salesClients.edit(data.infoClient.id, empresaId, body).then((res) => {
      createDismissableSnackbar(res.data, {
        variant: 'success',
      })
      setRender(!render)
      handleclose()
    })
    setLoading(false)
  }

  const horizontalTabs = [
    {
      id: 0,
      title: 'Inicio',
      color: 'primary',
    },
    {
      id: 1,
      title: 'CTA CTE Cliente',
      color: 'primary',
    },
    // {
    //   id: 2,
    //   title: "Archivos",
    //   color: "primary",
    // },
    {
      id: 2,
      title: 'Asientos',
      color: 'primary',
    },
    {
      id: 3,
      title: 'Historial',
      color: 'primary',
    },
  ]

  const renderTabs = (index) => {
    switch (index) {
      case 0:
        return (
          <HomeContent
            data={dataInfo}
            setData={setDataInfo}
            onDataChange={handleChange}
            changeStatus={changeStatus}
            dataBefore={dataBefore}
            empresaId={empresaId}
          />
        )
      case 1:
        return (
          <CtaCteClient
            data={dataInfo}
            setData={setDataInfo}
            dataBefore={dataBefore}
            setLoading={setLoading}
            empresaId={empresaId}
          />
        )
      // case 2:
      //   return (
      //     ""
      case 2:
        return <Asientos data={dataInfo} />
      case 3:
        return (
          <div style={{ height: '600px', overflow: 'auto' }}>
            <div style={{ height: '552px', overflow: 'auto' }}>
              <ComplexDialogHistory history={historyRows} disableScroll />
            </div>

            <div
              style={{
                position: 'sticky',
                bottom: 0,
                background: '#fff',
                padding: '10px 20px',
                borderTop: '1px solid #ddd',
                zIndex: 10,
              }}
            >
              <Box display="flex" justifyContent="flex-end">
                <Button
                  color="primary"
                  variant="outlined"
                  disabled={
                    !dataBefore.data.permissions.allowEdit ||
                    !dataBefore.data.permissions.allowEditParcial
                  }
                  onClick={() => {
                    setOpenFollowUpForm(true)
                    setFollowUpTouched(false)
                    setFollowUpDescription('')
                  }}
                >
                  Nuevo seguimiento
                </Button>
              </Box>
            </div>

            <DialogComponent
              open={openFollowUpForm}
              handleclose={() => {
                setOpenFollowUpForm(false)
                setFollowUpTouched(false)
              }}
              widthLarge={500}
              title="Nuevo seguimiento"
              disableChildHeight
              actions={
                <CancelConfirmButtons
                  onClickCancel={() => {
                    setOpenFollowUpForm(false)
                    setFollowUpTouched(false)
                  }}
                  onClickSubmit={handleConfirmFollowUp}
                  disabledSubmit={!isFollowUpValid || followUpLoading}
                  disabledCancel={followUpLoading}
                  isLoading={followUpLoading}
                />
              }
            >
              <TextField
                fullWidth
                label="Descripción"
                value={followUpDescription}
                required
                multiline
                minRows={3}
                onChange={(e) => setFollowUpDescription(e.target.value)}
                onBlur={() => setFollowUpTouched(true)}
                error={followUpTouched && !isFollowUpValid}
                helperText={
                  followUpTouched && !isFollowUpValid ? 'La descripción es obligatoria' : ''
                }
                variant="outlined"
                size="medium"
              />
            </DialogComponent>
          </div>
        )
      default:
        break
    }
  }

  const clientName =
    dataInfo?.infoClient?.nombre || dataInfo?.infoClient?.razon_social || 'Sin nombre'

  return (
    <>
      <ComplexDialog
        open={open}
        onClose={handleclose}
        tabs={horizontalTabs}
        title={
          <>
            {`${clientName} ID ${dataInfo.infoClient.id}`}
            {dataInfo.infoClient.estado === 'Activo' ? (
              <span
                style={{ color: '#f1f1f1', backgroundColor: '#3e984c' }}
                className={classes.estado}
              >
                {dataInfo.infoClient.estado.charAt(0).toUpperCase() +
                  dataInfo.infoClient.estado.slice(1).toLowerCase()}
              </span>
            ) : dataInfo.infoClient.estado === 'BLOQUEADO' ? (
              <span
                style={{ color: '#f1f1f1', backgroundColor: '#cc3030' }}
                className={classes.estado}
              >
                {dataInfo.infoClient.estado.charAt(0).toUpperCase() +
                  dataInfo.infoClient.estado.slice(1).toLowerCase()}
              </span>
            ) : dataInfo.infoClient.estado === 'INHABILITADO' ? (
              <span
                style={{ color: '#f1f1f1', backgroundColor: '#707070e6' }}
                className={classes.estado}
              >
                {dataInfo.infoClient.estado.charAt(0).toUpperCase() +
                  dataInfo.infoClient.estado.slice(1).toLowerCase()}
              </span>
            ) : (
              <span
                style={{ color: '#f1f1f1', backgroundColor: '#cc3030' }}
                className={classes.estado}
              >
                {dataInfo.infoClient.estado.charAt(0).toUpperCase() +
                  dataInfo.infoClient.estado.slice(1).toLowerCase()}
              </span>
            )}
          </>
        }
        subtitle={
          <div>
            {`Creado el ${new Date(dataInfo.infoClient.fecha_creacion).toLocaleDateString('es-AR')} por ${dataInfo.infoClient.usuario}`}
          </div>
        }
        children={renderTabs}
        actions={
          <ComplexDialogActions
            onClose={handleclose}
            handleSubmit={() => handleSubmit()}
            canEdit={
              fromOrders
                ? true
                : !dataBefore.data.permissions.allowEdit &&
                    !dataBefore.data.permissions.allowEditParcial
                  ? true
                  : dataInfo.infoClient.estado === 'INHABILITADO'
                    ? true
                    : dataInfo.canEdit
            }
            data={dataInfo}
            editMode={true}
            leftButton={{
              icon: <ExitToApp fontSize="small" />,
              title: 'Eliminar',
              variant: 'contained',
              disabled: !dataBefore.data.permissions.allowDelete,
              style: {
                background: dataBefore.data.permissions.allowDelete
                  ? 'rgb(244, 67, 54)'
                  : '#e0e0e0',
                color: dataBefore.data.permissions.allowDelete ? '#fff' : '#adadad',
              },
              onClick: () => {
                setOpen()
              },
            }}
          />
        }
      />

      {state.suspense && (
        <DialogComponent
          open={state.open}
          handleclose={state.loading ? null : setClose}
          widthLarge={500}
          title="¿Desea eliminar el siguiente cliente?"
          subtitle="¡Esta acción es irreversible!"
          disableChildHeight
          disableBackdropClick
          disableEscapeKeyDown
          actions={
            <CancelConfirmButtons
              onClickSubmit={() => handleDelete()}
              disabledCancel={state.loading}
              disabledSubmit={state.loading}
              isLoading={state.loading}
            />
          }
        />
      )}
    </>
  )
}

export default Edit
